import React, { useState } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  FormControl,
  FormLabel,
  RadioGroup,
  FormControlLabel,
  Radio,
  Box,
  Alert,
  Typography,
  LinearProgress,
  IconButton,
  Select,
  MenuItem,
  InputLabel,
  Checkbox,
  FormGroup,
  Link,
} from "@mui/material";
import { Close, CloudDownload, CloudUpload, Download, Upload, GetApp } from "@mui/icons-material";
import axios from "axios";
import { hostname, parseError } from "../../../utils";
import LoadingButton from "../../../components/LoadingButton";

interface ContentExportImportDialogProps {
  open: boolean;
  onClose: () => void;
  companyId: string;
  campaigns: any[];
  selectedContentIds?: string[];
  getToken: () => Promise<string>;
  onImportComplete?: () => void;
  eventListener: (event: any) => void;
}

const ContentExportImportDialog: React.FC<ContentExportImportDialogProps> = ({
  open,
  onClose,
  companyId,
  campaigns,
  selectedContentIds = [],
  getToken,
  onImportComplete,
  eventListener,
}) => {
  const [mode, setMode] = useState<"export" | "import">("export");
  const [selectedCampaignId, setSelectedCampaignId] = useState<string>("");
  const [statusFilter, setStatusFilter] = useState<string>("ideated");
  const [loading, setLoading] = useState(false);
  const [importFile, setImportFile] = useState<File | null>(null);
  const [csvData, setCsvData] = useState<string>("");
  const [csvPreview, setCsvPreview] = useState<any[]>([]);
  const [generateFullContent, setGenerateFullContent] = useState(false);
  const [importResults, setImportResults] = useState<any>(null);

  const handleExport = async () => {
    setLoading(true);
    try {
      const token = await getToken();
      const response = await axios.post(
        `${hostname}/content/export`,
        {
          companyId,
          // Use selected content IDs if available, otherwise use filters
          ...(selectedContentIds.length > 0
            ? { contentIds: selectedContentIds }
            : {
                filters: {
                  campaignId: selectedCampaignId || undefined,
                  status: statusFilter,
                },
              }),
        },
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
          responseType: "blob",
        }
      );

      // Create download link
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute(
        "download",
        `content_ideas_${new Date().toISOString().split("T")[0]}.csv`
      );
      link.click();
      window.URL.revokeObjectURL(url);

      eventListener({
        type: "SET_SUCCESS",
        message: "Content ideas exported successfully!",
      });
      onClose();
    } catch (error) {
      eventListener({
        type: "SET_ERROR",
        error: parseError(error),
      });
    } finally {
      setLoading(false);
    }
  };

  const parseCSVPreview = (csvContent: string) => {
    const lines = csvContent.split('\n');
    const headers = lines[0]?.split(',').map(h => h.trim());
    const dataRows = [];
    
    // Parse first few rows for preview
    for (let i = 1; i < Math.min(6, lines.length); i++) {
      if (lines[i].trim()) {
        dataRows.push(lines[i]);
      }
    }
    
    return {
      totalRows: lines.filter(l => l.trim()).length - 1, // Exclude header
      previewRows: dataRows.length,
      hasData: dataRows.length > 0
    };
  };

  const handleFileSelect = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (file) {
      if (!file.name.endsWith('.csv')) {
        eventListener({
          type: "SET_ERROR",
          error: "Please select a CSV file",
        });
        return;
      }

      setImportFile(file);
      
      // Read and parse the CSV file
      const reader = new FileReader();
      reader.onload = (e) => {
        const content = e.target?.result as string;
        setCsvData(content);
        
        // Parse preview info
        const preview = parseCSVPreview(content);
        setCsvPreview([preview]);
      };
      reader.readAsText(file);
    }
  };

  const downloadTemplate = () => {
    const template = `Title,Type,Category,Status,Funnel Stage,Topic Summary,Hook Option 1,Hook Option 2,Hook Option 3,Hook Option 4,Hook Option 5,Selected Hook,Publish Date,Campaign Name
,blog_post,,ideated,tofu,Write a 3-6 sentence description of your content topic here,Your first title option,Your second title option,Your third title option,Your fourth title option,Your fifth title option,Choose one of the hook options to generate full content,,
,social_post,,ideated,mofu,Another content idea description,Social media hook 1,Social media hook 2,Social media hook 3,,,Select this hook if you want to generate content,,`;
    
    const blob = new Blob([template], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.setAttribute('download', 'content_ideas_template.csv');
    document.body.appendChild(link);
    link.click();
    link.remove();
    window.URL.revokeObjectURL(url);
  };

  const handleImport = async () => {
    if (!csvData) {
      eventListener({
        type: "SET_ERROR",
        error: "No CSV data to import",
      });
      return;
    }

    setLoading(true);
    try {
      const token = await getToken();
      const response = await axios.post(
        `${hostname}/content/import`,
        {
          companyId,
          csvData,
          campaignId: selectedCampaignId || undefined,
          generateFullContent,
        },
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );

      setImportResults(response.data);
      
      if (response.data.imported > 0) {
        eventListener({
          type: "SET_SUCCESS",
          message: `Successfully imported ${response.data.imported} content ideas!`,
        });
        
        if (onImportComplete) {
          onImportComplete();
        }
        
        // Reset and close after a delay
        setTimeout(() => {
          onClose();
          setImportFile(null);
          setCsvData("");
          setCsvPreview([]);
          setImportResults(null);
        }, 2000);
      }
    } catch (error) {
      eventListener({
        type: "SET_ERROR",
        error: parseError(error),
      });
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    if (!loading) {
      setImportFile(null);
      setCsvData("");
      setCsvPreview([]);
      setImportResults(null);
      setSelectedCampaignId("");
      onClose();
    }
  };

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="md" fullWidth>
      <DialogTitle>
        <Box display="flex" justifyContent="space-between" alignItems="center">
          Export/Import Content Ideas
          <IconButton onClick={handleClose} size="small">
            <Close />
          </IconButton>
        </Box>
      </DialogTitle>
      <DialogContent>
        <FormControl component="fieldset" sx={{ mb: 3 }}>
          <FormLabel component="legend">Select Action</FormLabel>
          <RadioGroup
            row
            value={mode}
            onChange={(e) => setMode(e.target.value as "export" | "import")}
          >
            <FormControlLabel
              value="export"
              control={<Radio />}
              label={
                <Box display="flex" alignItems="center" gap={1}>
                  <CloudDownload />
                  Export to CSV
                </Box>
              }
            />
            <FormControlLabel
              value="import"
              control={<Radio />}
              label={
                <Box display="flex" alignItems="center" gap={1}>
                  <CloudUpload />
                  Import from CSV
                </Box>
              }
            />
          </RadioGroup>
        </FormControl>

        {mode === "export" ? (
          <Box>
            {selectedContentIds.length > 0 ? (
              <Alert severity="success" sx={{ mb: 2 }}>
                <Typography variant="body2">
                  <strong>Exporting {selectedContentIds.length} selected content item{selectedContentIds.length > 1 ? 's' : ''}</strong>
                </Typography>
                <Typography variant="caption">
                  The export will include only the content items you have selected.
                </Typography>
              </Alert>
            ) : (
              <>
                <FormControl fullWidth sx={{ mb: 2 }}>
                  <InputLabel>Campaign (Optional)</InputLabel>
                  <Select
                    value={selectedCampaignId}
                    label="Campaign (Optional)"
                    onChange={(e) => setSelectedCampaignId(e.target.value)}
                  >
                    <MenuItem value="">All Campaigns</MenuItem>
                    {campaigns.map((campaign) => (
                      <MenuItem key={campaign.id} value={campaign.id}>
                        {campaign.name}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>

                <FormControl fullWidth sx={{ mb: 2 }}>
                  <InputLabel>Status Filter</InputLabel>
                  <Select
                    value={statusFilter}
                    label="Status Filter"
                    onChange={(e) => setStatusFilter(e.target.value)}
                  >
                    <MenuItem value="">All Statuses</MenuItem>
                    <MenuItem value="ideated">Ideated Only</MenuItem>
                    <MenuItem value="draft">Draft</MenuItem>
                    <MenuItem value="published">Published</MenuItem>
                  </Select>
                </FormControl>
              </>
            )}

            <Alert severity="info" sx={{ mt: 2 }}>
              The CSV file will include all content ideas with their titles, types, and hook options.
              Share this file with your clients for review and approval.
            </Alert>
          </Box>
        ) : (
          <Box>
            <Alert severity="info" sx={{ mb: 2 }}>
              <Typography variant="body2" sx={{ mb: 1 }}>
                <strong>Import Instructions:</strong>
              </Typography>
              <Typography variant="body2" component="div">
                1. Download the template CSV file to see the required format<br/>
                2. Fill in your content ideas or edit the exported file from clients<br/>
                3. Upload the CSV file to import all content ideas at once<br/>
                4. Optionally select "Generate full content" to create complete content from selected hooks
              </Typography>
              <Button
                size="small"
                startIcon={<GetApp />}
                onClick={downloadTemplate}
                sx={{ mt: 1 }}
              >
                Download CSV Template
              </Button>
            </Alert>

            <Button
              variant="outlined"
              component="label"
              fullWidth
              startIcon={<Upload />}
              sx={{ mb: 2 }}
            >
              Select CSV File
              <input
                type="file"
                hidden
                accept=".csv"
                onChange={handleFileSelect}
              />
            </Button>

            {importFile && (
              <Alert severity="success" sx={{ mb: 2 }}>
                <Typography variant="body2">
                  <strong>Selected file:</strong> {importFile.name}
                </Typography>
                {csvPreview[0] && (
                  <Typography variant="body2">
                    Found {csvPreview[0].totalRows} rows to import
                  </Typography>
                )}
              </Alert>
            )}

            <FormControl fullWidth sx={{ mb: 2 }}>
              <InputLabel>Assign to Campaign (Optional)</InputLabel>
              <Select
                value={selectedCampaignId}
                label="Assign to Campaign (Optional)"
                onChange={(e) => setSelectedCampaignId(e.target.value)}
              >
                <MenuItem value="">Don't assign to campaign</MenuItem>
                {campaigns.map((campaign) => (
                  <MenuItem key={campaign.id} value={campaign.id}>
                    {campaign.name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            <FormGroup>
              <FormControlLabel
                control={
                  <Checkbox
                    checked={generateFullContent}
                    onChange={(e) => setGenerateFullContent(e.target.checked)}
                  />
                }
                label="Generate full content for ideas with selected hooks"
              />
            </FormGroup>

            {importResults && (
              <Alert
                severity={importResults.failed > 0 ? "warning" : "success"}
                sx={{ mt: 2 }}
              >
                <Typography variant="body2">
                  <strong>Import Results:</strong>
                </Typography>
                <Typography variant="body2">
                  Successfully imported: {importResults.imported} items
                </Typography>
                {importResults.failed > 0 && (
                  <Typography variant="body2" color="error">
                    Failed: {importResults.failed} items
                  </Typography>
                )}
              </Alert>
            )}
          </Box>
        )}

        {loading && <LinearProgress sx={{ mt: 2 }} />}
      </DialogContent>
      <DialogActions>
        <Button onClick={handleClose} disabled={loading}>
          Cancel
        </Button>
        {mode === "export" ? (
          <LoadingButton
            loading={loading}
            onClick={handleExport}
            variant="contained"
            startIcon={<Download />}
          >
            Export CSV
          </LoadingButton>
        ) : (
          <LoadingButton
            loading={loading}
            onClick={handleImport}
            variant="contained"
            startIcon={<CloudUpload />}
            disabled={!csvData}
          >
            Import CSV
          </LoadingButton>
        )}
      </DialogActions>
    </Dialog>
  );
};

export default ContentExportImportDialog;